import { Database, FileText, Image, Upload, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Input } from '#app/components/ui/input'

interface FileInputProps {
	initialFile?: string | File
	onFileChange: (file: File | null) => void
	acceptedFileTypes?: string
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>
	errors?: string[]
	description?: string
}

export function FileInput({
	initialFile,
	onFileChange,
	acceptedFileTypes = 'image/*,text/*,.pdb',
	inputProps,
	errors,
	description,
}: FileInputProps) {
	const [file, setFile] = useState<{
		type: string
		content: string
		name: string
		size: number
	} | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [isDragOver, setIsDragOver] = useState(false)
	const inputId =
		inputProps?.id ||
		`file-input-${Math.random().toString(36).substring(2, 11)}`

	useEffect(() => {
		if (initialFile) {
			if (typeof initialFile === 'string') {
				setFile({
					type: getFileType(initialFile),
					content: initialFile,
					name: initialFile,
					size: 0,
				})
			} else if (initialFile instanceof File) {
				const reader = new FileReader()
				reader.onload = e => {
					const content = e.target?.result as string
					setFile({
						type: getFileType(initialFile.name),
						content,
						name: initialFile.name,
						size: initialFile.size,
					})
				}
				reader.readAsDataURL(initialFile)
			}
		}
	}, [initialFile])

	const getFileType = (fileName: string): 'image' | 'pdb' | 'text' => {
		if (
			fileName.startsWith('data:image') ||
			fileName.startsWith('/resources/pmhc-images/') ||
			/\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName)
		)
			return 'image'
		if (
			fileName.startsWith('/resources/pdb-files/') ||
			fileName.endsWith('.pdb')
		)
			return 'pdb'
		return 'text'
	}

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return '0 Bytes'
		const k = 1024
		const sizes = ['Bytes', 'KB', 'MB', 'GB']
		const i = Math.floor(Math.log(bytes) / Math.log(k))
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
	}

	const handleFileChange = (selectedFile: File) => {
		const reader = new FileReader()
		reader.onload = e => {
			const content = e.target?.result as string
			setFile({
				type: getFileType(selectedFile.name),
				content,
				name: selectedFile.name,
				size: selectedFile.size,
			})
			onFileChange(selectedFile)

			// Also update the hidden input's files property for form validation
			if (fileInputRef.current) {
				const dataTransfer = new DataTransfer()
				dataTransfer.items.add(selectedFile)
				fileInputRef.current.files = dataTransfer.files
			}
		}
		reader.readAsDataURL(selectedFile)
	}

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = event.target.files?.[0]
		if (selectedFile) {
			handleFileChange(selectedFile)
		} else {
			// Clear the file when no file is selected
			setFile(null)
			onFileChange(null)
		}
	}

	const handleRemoveFile = () => {
		setFile(null)
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
			fileInputRef.current.files = null
		}
		onFileChange(null)
	}

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault()
		setIsDragOver(true)
	}

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault()
		setIsDragOver(false)
	}

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault()
		setIsDragOver(false)

		const droppedFile = e.dataTransfer.files[0]
		if (droppedFile) {
			handleFileChange(droppedFile)
			// Also update the hidden input's files when dropping
			if (fileInputRef.current) {
				const dataTransfer = new DataTransfer()
				dataTransfer.items.add(droppedFile)
				fileInputRef.current.files = dataTransfer.files
			}
		}
	}

	const getIcon = () => {
		if (acceptedFileTypes === 'image/*') {
			return <Image className="h-8 w-8 text-blue-500" />
		}
		if (acceptedFileTypes === 'chemical/x-pdb') {
			return <Database className="h-8 w-8 text-green-500" />
		}
		return <Upload className="h-8 w-8 text-gray-400" />
	}

	const renderFilePreview = () => {
		if (!file) return null

		switch (file.type) {
			case 'image':
				return (
					<div className="relative group h-48">
						<img
							src={file.content}
							alt="File preview"
							className="w-full h-full object-cover rounded-lg border"
						/>
						<div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
							<Button
								type="button"
								variant="destructive"
								size="sm"
								onClick={handleRemoveFile}
								className="bg-red-500 hover:bg-red-600 text-white"
							>
								<X className="h-4 w-4 mr-1" />
								Remove
							</Button>
						</div>
					</div>
				)
			case 'pdb':
				return (
					<div className="flex items-center justify-between p-6 bg-green-50 border border-green-200 rounded-lg h-48">
						<div className="flex items-center space-x-4">
							<Database className="h-16 w-16 text-green-600" />
							<div>
								<p className="font-medium text-green-800 text-lg">
									{file.name}
								</p>
								<p className="text-sm text-green-600">
									{formatFileSize(file.size)}
								</p>
							</div>
						</div>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={handleRemoveFile}
							className="text-green-600 hover:text-red-600 hover:bg-red-50"
						>
							<X className="h-4 w-4" />
						</Button>
					</div>
				)
			default:
				return (
					<div className="flex items-center justify-between p-6 bg-gray-50 border border-gray-200 rounded-lg h-48">
						<div className="flex items-center space-x-4">
							<FileText className="h-16 w-16 text-gray-600" />
							<div>
								<p className="font-medium text-gray-800 text-lg">{file.name}</p>
								<p className="text-sm text-gray-600">
									{formatFileSize(file.size)}
								</p>
							</div>
						</div>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={handleRemoveFile}
							className="text-gray-600 hover:text-red-600 hover:bg-red-50"
						>
							<X className="h-4 w-4" />
						</Button>
					</div>
				)
		}
	}

	return (
		<div className="space-y-3">
			{file ? (
				renderFilePreview()
			) : (
				<div
					className={`
						relative border-2 border-dashed rounded-lg p-8 transition-all h-48 flex items-center justify-center
						${
							isDragOver
								? 'border-blue-400 bg-blue-50'
								: 'border-gray-300 hover:border-gray-400'
						}
						${errors && errors.length > 0 ? 'border-red-300 bg-red-50' : 'bg-gray-50 hover:bg-gray-100'}
					`}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
				>
					<div className="text-center">
						<div className="flex justify-center mb-3">{getIcon()}</div>
						<label htmlFor={inputId} className="cursor-pointer">
							<span className="font-medium text-blue-600 hover:text-blue-500">
								Click to upload
							</span>
							<span className="text-gray-500"> or drag and drop</span>
						</label>
						{description && (
							<p className="text-sm text-gray-500 mt-2">{description}</p>
						)}
					</div>
				</div>
			)}

			<Input
				{...inputProps}
				id={inputId}
				type="file"
				className="hidden"
				onChange={handleInputChange}
				accept={acceptedFileTypes}
				ref={fileInputRef}
				aria-invalid={!!errors && errors.length > 0}
				aria-describedby={
					errors && errors.length > 0 ? `${inputId}-errors` : undefined
				}
			/>

			{errors && errors.length > 0 && (
				<div
					id={`${inputId}-errors`}
					className="text-sm text-red-600"
					role="alert"
				>
					{errors.map((error, index) => (
						<p key={index}>{error}</p>
					))}
				</div>
			)}
		</div>
	)
}
